import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Smartphone, TrendingUp, ArrowRight } from "lucide-react";
import { Logo } from "@/components/Bits";
import { APP_LINKS } from "@/data/site";

const PlayIcon = ({ size = 17 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden="true">
    <path d="M3.6 2.3 13.5 12 3.6 21.7c-.36-.2-.6-.6-.6-1.06V3.36c0-.46.24-.86.6-1.06Z" fill="#00E0FF" />
    <path d="M16.9 8.6 13.5 12l3.4 3.4 3.1-1.77c.9-.52.9-1.74 0-2.26L16.9 8.6Z" fill="#FFC400" />
    <path d="M3.6 2.3c.32-.18.72-.2 1.08 0L16.9 8.6 13.5 12 3.6 2.3Z" fill="#00F076" />
    <path d="M3.6 21.7 13.5 12l3.4 3.4L4.68 21.7c-.36.2-.76.18-1.08 0Z" fill="#FF3D57" />
  </svg>
);

export default function WelcomeModal() {
  const [open, setOpen] = useState(false);
  const [jobs, setJobs] = useState(4);
  const [avg, setAvg] = useState(250);

  useEffect(() => {
    let t;
    try {
      if (!sessionStorage.getItem("azo-welcome-seen")) {
        t = setTimeout(() => {
          setOpen(true);
          sessionStorage.setItem("azo-welcome-seen", "1");
        }, 700);
      }
    } catch (e) {
      /* ignore */
    }
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (!open) return;
    document.body.style.overflow = "hidden";
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const monthly = useMemo(() => jobs * avg * 26, [jobs, avg]);
  const fmt = (n) => "₹" + n.toLocaleString("en-IN");

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="welcome-modal"
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
        >
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setOpen(false)}
            data-testid="welcome-modal-backdrop"
          />

          <motion.div
            className="relative z-10 w-full max-w-sm max-h-[94vh] overflow-y-auto no-scrollbar rounded-3xl border border-azo-border bg-azo-surface shadow-azo"
            initial={{ opacity: 0, scale: 0.92, y: 18 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 8 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
          >
            {/* header */}
            <div className="relative px-5 pt-5 pb-4 text-center border-b border-azo-border">
              <button
                onClick={() => setOpen(false)}
                aria-label="Close"
                data-testid="welcome-modal-close"
                className="absolute right-3.5 top-3.5 grid h-8 w-8 place-items-center rounded-full bg-azo-surface2 text-azo-muted hover:bg-azo-primary hover:text-white transition-colors"
              >
                <X size={17} />
              </button>
              <div className="flex justify-center">
                <Logo className="h-9" testid="welcome-modal-logo" />
              </div>
              <h2 className="mt-2.5 font-heading font-extrabold text-xl leading-tight">
                Welcome to AzoApp 👋
              </h2>
              <p className="mt-0.5 text-[13px] text-azo-muted">Service book karo ya partner ban kar kamao.</p>
            </div>

            <div className="p-5 space-y-4">
              {/* 1. customer app */}
              <a
                href={APP_LINKS.customer}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="welcome-download-customer"
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-azo-primary px-4 py-3.5 text-[15px] font-heading font-semibold text-white shadow-azo transition-all duration-300 hover:bg-azo-primaryh"
              >
                <Smartphone size={18} /> Download Customer App <PlayIcon />
              </a>

              {/* 2. earning calculator + join partner */}
              <div className="rounded-2xl border border-azo-border bg-azo-surface2/50 p-4">
                <div className="flex items-center gap-2 mb-3">
                  <span className="grid h-7 w-7 place-items-center rounded-lg bg-azo-emerald/15 text-azo-emerald">
                    <TrendingUp size={16} />
                  </span>
                  <p className="font-heading font-bold text-sm">Partner Earning Calculator</p>
                </div>

                <div className="space-y-3">
                  <div>
                    <div className="flex items-center justify-between text-[13px] mb-1">
                      <span className="text-azo-muted">Jobs per day</span>
                      <span className="font-heading font-bold">{jobs}</span>
                    </div>
                    <input
                      type="range" min="1" max="12" value={jobs}
                      onChange={(e) => setJobs(Number(e.target.value))}
                      data-testid="calc-jobs-slider"
                      className="w-full accent-azo-primary cursor-pointer"
                    />
                  </div>
                  <div>
                    <div className="flex items-center justify-between text-[13px] mb-1">
                      <span className="text-azo-muted">Average per job</span>
                      <span className="font-heading font-bold">{fmt(avg)}</span>
                    </div>
                    <input
                      type="range" min="100" max="1200" step="50" value={avg}
                      onChange={(e) => setAvg(Number(e.target.value))}
                      data-testid="calc-avg-slider"
                      className="w-full accent-azo-primary cursor-pointer"
                    />
                  </div>
                </div>

                {/* monthly earning */}
                <div className="mt-3 flex items-center justify-between rounded-xl bg-azo-primary px-4 py-3 text-white">
                  <span className="text-xs opacity-85">Est. monthly earning</span>
                  <span className="font-heading font-extrabold text-2xl leading-none" data-testid="calc-result">
                    {fmt(monthly)}
                  </span>
                </div>

                {/* start earning -> join partner */}
                <p className="mt-3 text-center text-[13px] font-semibold text-azo-text">Start earning today</p>
                <a
                  href={APP_LINKS.partner}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="welcome-download-partner"
                  className="mt-2 flex w-full items-center justify-center gap-2 rounded-xl bg-azo-emerald px-4 py-3.5 text-[15px] font-heading font-semibold text-white shadow-azo transition-all duration-300 hover:brightness-95"
                >
                  Join As A Partner <ArrowRight size={17} />
                </a>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
